import { createSlice, PayloadAction, Slice } from "@reduxjs/toolkit";
import { RootState } from "utils/store";
import { Resources } from "models/interfaces";
import history from "utils/history";

export interface Primary {
  id: string;
  email: string;
  name: string;
  officePhone: string;
  lastLogin: any;
  isTrial: boolean;
}

export interface AuthState {
  authenticated: boolean;
  accessToken: string;
  refreshToken: string;
  loading: boolean;
  userName: string;
  userId: number;
  email: string;
  userRole: string;
  resources: Resources[];
  profileImage: string;
  signature: string;
  type: string;
  account: string;
  /** Human-readable account name; `account` is the tenant key it falls back to. */
  accountDisplayName: string;
  isTrial: false;
  lastVisitedLink: string;
  pwdExpiryEnable: boolean;
  pwdExpiryRemainingDays: number;
  isSsoLogin: boolean;
  activeWorkspaceId: string;
  workspacePermissions: {
    scoped: boolean;
    admin: boolean;
    permissions: string[];
  };
}

const initialState: AuthState = {
  authenticated: false,
  accessToken: "",
  refreshToken: "",
  loading: false,
  userName: "",
  email: "",
  userId: 0,
  userRole: "",
  resources: [],
  profileImage: "",
  signature: "",
  type: "",
  account: "",
  accountDisplayName: "",
  isTrial: false,
  lastVisitedLink: "",
  pwdExpiryEnable: false,
  pwdExpiryRemainingDays: 0,
  isSsoLogin: false,
  activeWorkspaceId: "",
  workspacePermissions: { scoped: false, admin: false, permissions: [] },
};

export const authSlice: Slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginAction: (
      state,
      action: PayloadAction<{
        authenticated: boolean;
        accessToken: string;
        refreshToken: string;
        userName: string;
        email: string;
        userRole: string;
        resources: Resources[];
        profileImage: string;
        signature: string;
        userId: number;
        type: string;
        deviceId: string;
        account: string;
        accountDisplayName?: string;
        isTrial: boolean;
        lastVisitedLink: string;
        pwdExpiryEnable: boolean;
        pwdExpiryRemainingDays: number;
        isSsoLogin?: boolean;
      }>,
    ) => {
      state.authenticated = action.payload.authenticated;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.userName = action.payload.userName;
      state.email = action.payload.email;
      state.userRole = action.payload.userRole;
      state.resources = action.payload.resources;
      state.profileImage = action.payload.profileImage;
      state.signature = action.payload.signature;
      state.userId = action.payload.userId;
      state.type = action.payload.type;
      state.deviceId = action.payload.deviceId;
      state.account = action.payload.account;
      state.accountDisplayName = action.payload.accountDisplayName ?? "";
      state.isTrial = action.payload.isTrial;
      state.lastVisitedLink = action.payload.lastVisitedLink;
      state.pwdExpiryEnable = action.payload.pwdExpiryEnable;
      state.pwdExpiryRemainingDays = action.payload.pwdExpiryRemainingDays;
      state.isSsoLogin = action.payload.isSsoLogin ?? false;
    },
    addLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    logOutAction: (state) => {
      state.authenticated = false;
      state.loading = false;
      state.accessToken = "";
      state.userId = "";
      state.userName = "";
      state.resources = [];
      state.profileImage = "";
      state.signature = "";
      state.userId = "";
      state.type = "";
      state.deviceId = "";
      state.accountDisplayName = "";
      state.isSsoLogin = false;
      state.activeWorkspaceId = "";
      state.workspacePermissions = {
        scoped: false,
        admin: false,
        permissions: [],
      };
    },
    updateAccountDisplayName: (state, action: PayloadAction<string>) => {
      state.accountDisplayName = action.payload;
    },
    setLastVisitedLink: (state, action: PayloadAction<any>) => {
      state.lastVisitedLink = action.payload;
    },
    setActiveWorkspace: (state, action: PayloadAction<string>) => {
      state.activeWorkspaceId = action.payload;
    },
    setWorkspacePermissions: (
      state,
      action: PayloadAction<{
        scoped: boolean;
        admin: boolean;
        permissions: string[];
      }>,
    ) => {
      state.workspacePermissions = action.payload;
    },
    updateAccessToken: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
    },
    updateRefreshToken: (
      state,
      action: PayloadAction<{ refreshToken: string }>,
    ) => {
      state.refreshToken = action.payload.refreshToken;
    },
    makeAppDrawerHide: (state, action: PayloadAction<boolean>) => {
      state.hideAppDrawer = action.payload;
    },
    updateUserName: (state, action: PayloadAction<string>) => {
      state.userName = action.payload;
    },
    updateEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    updateProfileImage: (state, action: PayloadAction<string>) => {
      state.profileImage = action.payload;
    },
    updateSignature: (state, action: PayloadAction<string>) => {
      state.signature = action.payload;
    },
    addHealthWorkerId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    updateDeviceId: (state, action: PayloadAction<string>) => {
      state.deviceId = action.payload;
    },
    setAuthToken: (state, action: PayloadAction<any>) => {
      state.authenticated = action.payload.authenticated ?? false;
      state.accessToken = action.payload.accessToken ?? "";
      state.refreshToken = action.payload.refreshToken ?? "";
      state.userEmail = action.payload.email ?? state.userEmail;
      state.userName = action.payload.name ?? state.userName;
      state.account = action.payload.account ?? state.account;
      state.accountDisplayName =
        action.payload.accountDisplayName ?? state.accountDisplayName;
      state.userRole = action.payload.userRole ?? "";
      state.roleId = action.payload.roleId ?? "";
      state.resources = action.payload.resources ?? {};
      state.pwdExpiryEnable = action.payload.pwdExpiryEnable ?? false;
      state.multiFactorLogin = action.payload.multiFactorLogin ?? false;
      state.pwdExpiryRemainingDays =
        action.payload.pwdExpiryRemainingDays >= 0
          ? action.payload.pwdExpiryRemainingDays
          : 999;
    },
  },
});

export const {
  loginAction,
  logOutAction,
  setLastVisitedLink,
  addLoading,
  makeAppDrawerHide,
  updateUserName,
  updateEmail,
  updateAccessToken,
  updateProfileImage,
  addHealthWorkerId,
  updateDeviceId,
  updateRefreshToken,
  updateSignature,
  setAuthToken,
  setActiveWorkspace,
  setWorkspacePermissions,
  updateAccountDisplayName,
} = authSlice.actions;

export const selectAuthenticated = (state: RootState) =>
  state.auth.authenticated;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;
export const selectUserRole = (state: RootState) => state.auth.userRole;
export const selectId = (state: RootState) => state.auth.userId;
export const selectResources = (state: RootState) => state.auth.resources;
export const selectLoading = (state: RootState) => state.auth.loading;
export const selectUserName = (state: RootState) => state.auth.userName;
export const selectEmail = (state: RootState) => state.auth.email;
export const selectProfileImage = (state: RootState) => state.auth.profileImage;
export const selectSignature = (state: RootState) => state.auth.signature;
export const selectType = (state: RootState) => state.auth.type;
export const selectDeviceId = (state: RootState) => state.auth.deviceId;
export const selectAccount = (state: RootState) => state.auth.account;
export const selectAccountDisplayName = (state: RootState) =>
  state.auth.accountDisplayName;
export const isTrial = (state: RootState) => state.auth.isTrial;
export const selectLastVisitedLink = (state: RootState) =>
  state.auth.lastVisitedLink;
export const selectIsSsoLogin = (state: RootState) => state.auth.isSsoLogin;
export const selectActiveWorkspace = (state: RootState) =>
  state.auth.activeWorkspaceId;
export const selectWorkspacePermissions = (state: RootState) =>
  state.auth.workspacePermissions;

export default authSlice.reducer;
